import * as React from "react";
import { getLuminance } from "polished";
import {
  SMALL_SCREEN_WIDTH_THRESHOLD,
  SMALL_SCREEN_HEIGHT_THRESHOLD,
} from "./constants";

export function isPresent<T>(t: T | undefined | null | void): t is T {
  return t !== undefined && t !== null;
}

export function isLightColor(color: string): boolean {
  return getLuminance(color) > 0.179;
}

export function isInternalUrl(url: string) {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname === window.location.hostname;
  } catch (e) {
    return false;
  }
}

export function getLinkTargetForHostPageLinks(url: string) {
  if (isInternalUrl(url)) {
    return "_self";
  }
  return "_blank";
}

export function addUtmParamsToUrl(url: string, campaign: string) {
  if (!("URLSearchParams" in window)) {
    return url;
  }
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.set("utm_medium", "social");
    urlObj.searchParams.set("utm_source", "grow.me");
    urlObj.searchParams.set("utm_campaign", campaign);
    return urlObj.toString();
  } catch (e) {
    return url;
  }
}

export function decodeHtml(html: string) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

/**
 * Converts the number of seconds until a token expires into the utc time stamp
 * @param expiresIn Number of seconds until a token expires
 */
export function convertExpiresInToUtcTime(expiresIn: number | string): string {
  return new Date(Date.now() + Number(expiresIn) * 1000).toUTCString();
}

export function getCookieValue(name: string) {
  var b = document.cookie.match(`(^|;)\\s*${name}\\s*=\\s*([^;]+)`);
  return b ? b.pop() : "";
}

export function refineStringToLiteralUnion<T extends string>(
  value: string | null | undefined,
  possibleValues: readonly T[]
): T | null {
  if (possibleValues.includes(value as T)) {
    return value as T;
  }
  return null;
}

export function getQueryParam(param: string) {
  return new URLSearchParams(window.location.search).get(param);
}

export function useContainerDimensions(
  ref?: React.MutableRefObject<HTMLElement | null>
) {
  const [dimensions, setDimensions] = React.useState({
    height: ref && ref.current ? ref.current.clientHeight : window.innerHeight,
    width: ref && ref.current ? ref.current?.clientWidth : window.innerWidth,
  });
  React.useEffect(() => {
    const setDimensionsListener = () => {
      setDimensions({
        height:
          ref && ref.current ? ref.current.clientHeight : window.innerHeight,
        width:
          ref && ref.current ? ref.current?.clientWidth : window.innerWidth,
      });
    };
    setDimensionsListener();
    window.addEventListener("resize", setDimensionsListener);
    return () => {
      window.removeEventListener("resize", setDimensionsListener);
    };
  }, [ref]);
  return dimensions;
}

export function useIsSmallScreen(mode: "width" | "height" | "widthAndHeight") {
  const windowDimensions = useContainerDimensions();
  switch (mode) {
    case "widthAndHeight":
      return (
        windowDimensions.width <= SMALL_SCREEN_WIDTH_THRESHOLD &&
        windowDimensions.height <= SMALL_SCREEN_HEIGHT_THRESHOLD
      );
    case "height":
      return windowDimensions.height <= SMALL_SCREEN_HEIGHT_THRESHOLD;
    case "width":
      return windowDimensions.width <= SMALL_SCREEN_WIDTH_THRESHOLD;
    default:
      return windowDimensions.width <= SMALL_SCREEN_WIDTH_THRESHOLD;
  }
}
